import { useMemo } from 'react'

import { lineStrings } from 'constants/strings'
import type { TRichMenuBounds, TRichMenuTemplate } from 'types'

type RichMenuGridPreviewProps = {
  template: TRichMenuTemplate | undefined
  imageUrl: string | null
  showOutlines: boolean
}

function gridClassFor(areaCount: number) {
  if (areaCount <= 1) return 'grid-cols-1 grid-rows-1'
  if (areaCount === 2) return 'grid-cols-2 grid-rows-1'
  if (areaCount === 3) return 'grid-cols-3 grid-rows-1'
  if (areaCount === 6) return 'grid-cols-3 grid-rows-2'
  return 'grid-cols-2 grid-rows-2'
}

function useLayoutCells(
  template: TRichMenuTemplate | undefined,
): { mode: 'bounds' | 'grid'; bounds?: TRichMenuBounds[]; gridClass?: string } {
  return useMemo(() => {
    if (!template) {
      return { mode: 'grid', gridClass: gridClassFor(0) }
    }
    const { areaKeys, bounds, size } = template
    if (
      bounds.length > 0 &&
      bounds.length === areaKeys.length &&
      size.width > 0 &&
      size.height > 0
    ) {
      return { mode: 'bounds', bounds }
    }
    return { mode: 'grid', gridClass: gridClassFor(areaKeys.length) }
  }, [template])
}

export function RichMenuGridPreview({
  template,
  imageUrl,
  showOutlines,
}: RichMenuGridPreviewProps) {
  const areaKeys = template?.areaKeys ?? []
  const layout = useLayoutCells(template)

  const aspectStyle = template
    ? {
      aspectRatio: `${template.size.width} / ${template.size.height}`,
    }
    : undefined

  const cellClass = 'flex items-center justify-center border-2 border-emerald-500/80 bg-emerald-500/10 text-xs font-bold text-emerald-800'

  return (
    <div
      className="relative w-full overflow-hidden rounded-none border-x-0 border-b-0 border-t border-border/80 bg-surface"
      style={aspectStyle}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-[10px] text-muted">
          {lineStrings.management.richMenu.create.imageHint}
        </div>
      )}
      {layout.mode === 'bounds' && layout.bounds && template ? (
        <div
          className={`pointer-events-none absolute inset-0 z-10 ${showOutlines ? 'opacity-100' : 'opacity-0'}`}
        >
          {layout.bounds.map((b, i) => {
            const key = areaKeys[i] ?? String(i)
            const { width: W, height: H } = template.size
            return (
              <div
                key={`${key}-${i}`}
                className={cellClass}
                style={{
                  position: 'absolute',
                  left: `${(b.x / W) * 100}%`,
                  top: `${(b.y / H) * 100}%`,
                  width: `${(b.width / W) * 100}%`,
                  height: `${(b.height / H) * 100}%`,
                  boxSizing: 'border-box',
                }}
              >
                {key}
              </div>
            )
          })}
        </div>
      ) : (
        <div
          className={`pointer-events-none absolute inset-0 z-10 grid ${layout.gridClass ?? ''} gap-px 
          ${showOutlines ? 'opacity-100' : 'opacity-0'}`}
        >
          {areaKeys.map((key) => (
            <div key={key} className={cellClass}>
              {key}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
